import moment from 'moment';
import { isPrio, isRecovering } from 'utils/shipper';

const ANY_ROUTE = 'ANY';

const PICKUP = 'PICKUP';
const DROPOFF = 'DROPOFF';

const groupItinerariesByDate = (itineraries) => {
  return itineraries?.reduce((acc, itinerary) => {
    const { start_time_from } = itinerary;
    const formattedDate = moment(start_time_from).format('YYYY-MM-DD');
    return {
      ...acc,
      [formattedDate]: [...(acc[formattedDate] || []), itinerary],
    };
  }, {});
};

const isExtraPresent = (extra, extras = []) => extras.indexOf(extra) !== -1;

const isLocked = (itinerary) => {
  if (!itinerary) {
    return false;
  }

  return !itinerary.need_manual_dispatching;
};

const isUpgrade = (itinerary) => !!itinerary?.upgrade_journey;
const isAssignedToAgent = (itinerary) => !!itinerary?.owner?.id;

const cellWidth = (minWidth, maxWidth) => ({
  style: {
    minWidth: minWidth,
    maxWidth: maxWidth ? maxWidth : minWidth,
    flex: 1,
  },
});

const getDispatchingHeaderDate = (date, t) => {
  const today = moment();
  const tomorrow = moment().add(1, 'day');
  const day = moment(date);

  if (today.isSame(day, 'day')) {
    return t.today;
  }

  if (tomorrow.isSame(day, 'day')) {
    return t.tomorrow;
  }

  return day.format(t.otherDay);
};

const getMainShipper = (shippers = []) => {
  if (!shippers.length) {
    return null;
  }

  const firstPrioShipper = shippers.find((shipper) => shipper.is_prio);
  const firstFtbShipper = shippers.find((shipper) => shipper.is_ftb);

  if (!firstPrioShipper && !firstFtbShipper) {
    return shippers[0] || {};
  }

  return firstPrioShipper || firstFtbShipper;
};

export const getAppointmentValues = (itinerary) => {
  const safeStops = itinerary?.stops || [];
  const stopsWithRequiredAppointment = safeStops.filter((stop) => stop.appointment_required);
  const stopsWithAppointmentTimes = safeStops.filter((stop) => stop.has_appointment_time);

  const appointmentRequired = stopsWithRequiredAppointment.length > 0;
  const allStopsHasAppointmentTime =
    stopsWithRequiredAppointment.length === stopsWithAppointmentTimes.length;

  const isPendingAppointment = appointmentRequired && !allStopsHasAppointmentTime;
  const isAppointmentSet = appointmentRequired && allStopsHasAppointmentTime;

  return {
    isPendingAppointment,
    isAppointmentSet,
  };
};

const addTagsPropertiesToShipper = (shipper) => ({
  ...shipper,
  is_prio: isPrio(shipper),
  is_ftb: shipper?.sales_attributes?.is_ftb,
  is_recovering: isRecovering(shipper),
});

const mapItinerariesForDispatchingCommon = (itineraries) => {
  return itineraries?.map((itinerary) => {
    const shippers = itinerary?.shippers?.map((shipper) => addTagsPropertiesToShipper(shipper));

    return {
      ...itinerary,
      shipper_orders: (itinerary.shipper_orders || []).map((shipperOrder) => ({
        ...shipperOrder,
        shipper: addTagsPropertiesToShipper(shipperOrder.shipper),
      })),
      shipper: getMainShipper(shippers),
      number_of_shippers: shippers?.length - 1,
      ...getAppointmentValues(itinerary),
    };
  });
};

const getRouteFilter = (route) => {
  return route?.map((routeFilter) => `${routeFilter.value}->${routeFilter.value}`).join(',');
};

const getRouterFilterForAnyValues = (route) => {
  return route
    ?.map((routeFilter) => `${routeFilter.value}->${ANY_ROUTE},${ANY_ROUTE}->${routeFilter.value}`)
    .join(',');
};

const getFakeItineraryForShipperTooltipBasedOnStop = (stop) => {
  if (!!stop?.shipper_orders?.length) {
    return {
      shipper_orders: stop.shipper_orders.map((shipperOrder) => ({
        ...shipperOrder,
        shipper: addTagsPropertiesToShipper(shipperOrder.shipper),
      })),
    };
  }

  return null;
};

const mergeItinerariesAndOfferStatus = (itineraries, offerStatus) =>
  (itineraries || []).map((itinerary) => ({
    ...itinerary,
    ...((offerStatus || {})[itinerary.id] || {}),
  }));

export {
  groupItinerariesByDate,
  isExtraPresent,
  isLocked,
  isUpgrade,
  isAssignedToAgent,
  cellWidth,
  getDispatchingHeaderDate,
  mapItinerariesForDispatchingCommon,
  getRouteFilter,
  getRouterFilterForAnyValues,
  getFakeItineraryForShipperTooltipBasedOnStop,
  mergeItinerariesAndOfferStatus,
  PICKUP,
  DROPOFF,
};
